import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import apiService from '../services/api';
import TempStart from './TempStart';
import { useShellUser } from '../components/AppShell';
import { formatCompactCountdown, formatDisplayNumber, shouldShowDream } from '../utils/dreamSummary';
import { BoostCallout, BoostChip, BoostTile, DreamWeek } from '../components/DreamBoostBits';
import { networkBannerBoost } from '../utils/dreamBoost';
import { shareReferral } from '../utils/referralShare';
import { prizeRulesUrl } from '../utils/officialRules';
import { ReactComponent as TicketIcon } from '../assets/icons/ticket-entries.svg';
import { ReactComponent as GiftIcon } from '../assets/icons/icon-gift-outline.svg';
import { ReactComponent as LocationIcon } from '../assets/icons/icon-location.svg';
import { ReactComponent as InfoIcon } from '../assets/icons/info-outline.svg';
import { ReactComponent as InviteIcon } from '../assets/icons/invite-friend.svg';
import '../styles/Dream.css';

// The embed's Dream page (#/dream and #/dream/drops/<id>) for fan web: the
// prize draw or one bonus drop, the fan's entries, what the prize is, how the
// draw works, recent winners and the rules note. Same summary, same rules.

function instantMs(iso) {
  const ms = iso ? new Date(iso).getTime() : NaN;
  return Number.isFinite(ms) ? ms : null;
}

function prizeImage(prize) {
  if (!prize) return '';
  return prize.cover_image_url || (prize.image_urls && prize.image_urls[0]) || '';
}

/** An announced drop counts down to its opening, any other prize to its end. */
function countdownIso(prize, period, upcoming) {
  if (upcoming) return (prize && (prize.dream_starts_at || prize.starts_at)) || null;
  return (prize && (prize.countdown_target || prize.dream_ends_at)) || (period && period.ends_at) || null;
}

/** Only an open drop that takes entries and that the fan has not entered offers Enter. */
function canEnterDrop(drop) {
  return !!drop && drop.requires_opt_in === true && drop.opted_in !== true;
}

function isUpcomingDrop(summary, drop) {
  const id = String((drop && (drop.id || drop.prize_id)) || '');
  if (!id) return false;
  return (summary.upcoming_bonus_drops || []).some((item) => String(item.id || item.prize_id) === id);
}

function findListedDrop(summary, dropId) {
  const lists = [summary.bonus_drops || [], summary.upcoming_bonus_drops || []];
  for (const list of lists) {
    const hit = list.find((item) => String(item.id || item.prize_id) === String(dropId));
    if (hit) return hit;
  }
  return null;
}

function opensLabel(iso) {
  const date = iso ? new Date(iso) : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  return `Opens ${date.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })}`;
}

const ENTER_ERRORS = {
  program_off: 'The Dream program is off at this school',
  no_opt_in: 'This prize does not take entries',
  closed: 'This draw has already happened',
  not_open: 'This drop has not opened yet',
  excluded: 'This account cannot enter draws',
  not_found: 'Prize not found',
};

function enterErrorMessage(err) {
  const body = err?.response?.data || {};
  const detail = typeof body.detail === 'string' ? body.detail.trim() : '';
  if (detail) return detail;
  return ENTER_ERRORS[body.code] || 'Could not enter the drop';
}

function TextPair({ title, body }) {
  return (
    <div className="dream-text">
      <div className="dream-text-title">{title}</div>
      {body ? <div className="dream-text-body">{body}</div> : null}
    </div>
  );
}

function Hero({ prize, period, badge, upcoming, now }) {
  const image = prizeImage(prize);
  const endsMs = instantMs(countdownIso(prize, period, upcoming));
  const title = (prize && prize.title) || (period && period.name) || 'Dream Prize';
  const left = endsMs ? formatCompactCountdown(endsMs, now, upcoming ? 'Open' : 'Ended') : '';
  return (
    <div className="dream-hero">
      {image ? <img className="dream-hero-img" src={image} alt="" /> : <div className="dream-hero-img is-empty" />}
      <div className="dream-hero-shade" aria-hidden="true" />
      <Link to="/rewards" className="dream-back" aria-label="Back to Rewards">
        <svg viewBox="0 0 8 14" aria-hidden="true">
          <path d="M6.5 1.5 1.5 7l5 5.5" />
        </svg>
      </Link>
      {left ? (
        <div className="dream-timer">
          <svg viewBox="0 0 16 16" aria-hidden="true">
            <circle cx="8" cy="8" r="5.6" />
            <path d="M8 4.9V8l2.2 1.4" />
          </svg>
          {upcoming ? `Opens in ${left.replace(/ left$/, '')}` : left}
        </div>
      ) : null}
      <div className="dream-hero-copy">
        <div className="dream-badge">
          <i aria-hidden="true" />
          {badge}
        </div>
        <h1 className="dream-title">{title}</h1>
      </div>
    </div>
  );
}

function EntriesCard({ summary, drop, upcoming, onEnter, entering, error, now }) {
  const entries = Number(summary.entries) || 0;
  let label = 'Your entries in this draw';
  let sub = 'Shop, earn entries and get a chance to win!';
  let action = null;
  const earnBtn = (
    <Link className="dream-btn" to="/shop">
      Find places to earn
    </Link>
  );
  if (drop && upcoming) {
    sub = opensLabel(drop.dream_starts_at || drop.starts_at) || 'This drop has not opened yet.';
    action = earnBtn;
  } else if (drop) {
    if (drop.opted_in) {
      label = 'Your entries in this drop';
      sub = 'You’re in. Your entries count when the drop closes.';
      action = (
        <>
          <div className="dream-entered">Entered</div>
          {earnBtn}
        </>
      );
    } else if (!canEnterDrop(drop)) {
      label = 'Your entries in this drop';
      sub = 'Your entries count automatically.';
      action = earnBtn;
    } else {
      label = 'Entries you could enter with';
      sub = 'You’re not in this drop yet. These entries only count once you enter.';
      action = (
        <>
          <button type="button" className="dream-btn" onClick={onEnter} disabled={entering}>
            {entering ? 'Entering…' : entries > 0 ? 'Enter the drop' : 'Enter'}
          </button>
          {earnBtn}
        </>
      );
    }
  } else {
    action = earnBtn;
  }
  return (
    <section className="dream-entries-card" aria-label="Your entries">
      <div className="dream-entries-group">
        <div className="dream-entries-label">
          <span className="dream-entries-label-main">
            <TicketIcon className="dream-entries-icon" aria-hidden="true" />
            {label}
          </span>
          <BoostChip summary={summary} now={now} />
        </div>
        <div className="dream-entries-num">{formatDisplayNumber(entries)}</div>
      </div>
      <div className="dream-entries-cta">
        {/* The boost tile while a school-wide boost runs; otherwise the callout
            for any other running boost (a merchant's own, say). */}
        {networkBannerBoost(summary, now) && summary.at_entry_cap !== true ? (
          <BoostTile summary={summary} now={now} />
        ) : (
          <BoostCallout summary={summary} now={now} />
        )}
        <p className="dream-entries-sub">{sub}</p>
        {error ? <p className="dream-entries-error">{error}</p> : null}
        {action}
      </div>
    </section>
  );
}

const INVITE_NOTES = {
  copied: 'Copied. Paste it into a message.',
  failed: 'Could not share. Try again.',
};

// The invite row of the embed's Dream page: one tap opens the share sheet with the fan's code.
function InviteRow() {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');

  const onInvite = async () => {
    if (busy) return;
    setBusy(true);
    setNote('');
    try {
      const referral = await apiService.getReferralSummary();
      setNote(INVITE_NOTES[await shareReferral(referral.share_text)] || '');
    } catch (_error) {
      setNote('Could not load your referral code. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button type="button" className="dream-invite" onClick={onInvite} disabled={busy}>
        <InviteIcon className="dream-invite-icon" aria-hidden="true" />
        <TextPair title="Invite a friend" body="Earn rewards for a qualifying referral." />
        <svg className="dream-invite-chevron" viewBox="0 0 8 14" aria-hidden="true">
          <path d="M1.5 1.5 6.5 7l-5 5.5" />
        </svg>
      </button>
      {note ? <p className="dream-invite-note">{note}</p> : null}
    </>
  );
}

function SectionHeader({ title }) {
  return (
    <div className="dream-section-header">
      <div className="dream-section-title">{title}</div>
    </div>
  );
}

function Details({ prize, schoolName }) {
  if (!prize) return null;
  const rows = [];
  const included = prize.description || prize.cover_text || '';
  if (included) {
    rows.push(
      <div className="dream-info-row" key="included">
        <GiftIcon className="dream-info-icon" aria-hidden="true" />
        <TextPair title="What’s included" body={included} />
      </div>
    );
  }
  const location = String(prize.location || '').trim() || schoolName;
  if (location) {
    rows.push(
      <div className="dream-info-row" key="location">
        <LocationIcon className="dream-info-icon" aria-hidden="true" />
        <TextPair title="Location" body={location} />
      </div>
    );
  }
  if (!rows.length) return null;
  return (
    <section className="dream-section">
      <SectionHeader title="Prize details" />
      <div className="dream-panel">{rows}</div>
    </section>
  );
}

function HowItWorks({ drop }) {
  const steps = drop
    ? [
        ['Enter the drop', 'Tap Enter the drop before it closes.'],
        ['Your entries count', 'Every entry you have earned this period counts in the drop.'],
        ['Get a chance to win', 'The winner is drawn when the drop closes.'],
      ]
    : [
        ['Shop in-network with your linked card, or shop in-app', 'Pay at a participating partner.'],
        ['Earn entries automatically', 'Qualifying purchases add entries to this prize draw.'],
        ['Get a chance to win', 'Winners are selected after the draw closes.'],
      ];
  return (
    <section className="dream-section">
      <SectionHeader title="How does it work?" />
      <div className="dream-steps">
        {steps.map(([title, body], i) => (
          <div className="dream-step" key={title}>
            <span className="dream-step-num">{i + 1}</span>
            <TextPair title={title} body={body} />
          </div>
        ))}
      </div>
    </section>
  );
}

function Winners({ winners }) {
  const rows = (Array.isArray(winners) ? winners : []).filter((w) => w && w.draw_kind !== 'bonus_drop');
  if (!rows.length) return null;
  return (
    <section className="dream-section">
      <SectionHeader title="Recent winners" />
      <div className="dream-panel">
        {rows.map((w) => (
          <div className="dream-info-row" key={w.prize_id || `${w.winner_name}-${w.drawn_at}`}>
            <TextPair title={w.is_you ? 'You' : w.winner_name || 'A fan'} body={w.title || ''} />
          </div>
        ))}
      </div>
    </section>
  );
}

function RulesNote({ summary, schoolId, drop }) {
  const school = summary.rules_url || (schoolId ? `/official-rules/?schoolId=${encodeURIComponent(schoolId)}` : '/official-rules/');
  // A bonus drop links its own rules; the prize draw page, the rules of the period's prize draws.
  const rules = drop ? prizeRulesUrl(school, drop.id || drop.prize_id) : school;
  return (
    <div className="dream-note">
      <InfoIcon className="dream-note-icon" aria-hidden="true" />
      <p className="dream-note-text">
        No purchase necessary.
        <br />
        <a href={rules} target="_blank" rel="noreferrer">
          Official rules
        </a>
      </p>
    </div>
  );
}

function DreamPage() {
  const { dropId } = useParams();
  const navigate = useNavigate();
  const { user } = useShellUser();
  const [summary, setSummary] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [entering, setEntering] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    let cancelled = false;
    Promise.all([
      apiService.getDreamSummary(),
      apiService.getReferralCampaign().catch(() => null),
    ])
      .then(([data, referral]) => {
        if (cancelled) return;
        setSummary(data);
        setShowInvite(!!(referral && referral.campaign));
        setLoaded(true);
      })
      .catch(() => {
        if (!cancelled) {
          setShowInvite(false);
          setLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => load(), [load]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const onEnter = async (drop) => {
    setEntering(true);
    setError('');
    try {
      await apiService.enterDreamPrize(drop.id);
      load();
    } catch (err) {
      setError(enterErrorMessage(err));
    } finally {
      setEntering(false);
    }
  };

  if (!loaded) {
    return (
      <div className="dream-page">
        <p className="dream-loading">Loading…</p>
      </div>
    );
  }
  // No program or no period: there is nothing to show, as on the embed.
  if (!shouldShowDream(summary)) return <Navigate to="/rewards" replace />;

  const drop = dropId ? findListedDrop(summary, dropId) : null;
  // A drop that has closed or was never this school's falls back to the draw.
  if (dropId && !drop) return <Navigate to="/dream" replace />;

  const prize = drop || (summary.prizes || [])[0] || null;
  const upcoming = isUpcomingDrop(summary, drop);
  const schoolName = String(user?.active_school?.name || '').trim();
  const schoolId = user?.active_school?.id || '';

  return (
    <div className="dream-page">
      <Hero prize={prize} period={summary.period} badge={drop ? 'Bonus drop' : 'Prize draw'} upcoming={upcoming} now={now} />
      <div className="dream-body">
        <div className="dream-main">
          <Details prize={prize} schoolName={schoolName} />
          <HowItWorks drop={drop} />
          <Winners winners={summary.recent_winners} />
          <RulesNote summary={summary} schoolId={schoolId} drop={drop} />
        </div>
        <aside className="dream-side">
          <EntriesCard
            summary={summary}
            drop={drop}
            upcoming={upcoming}
            entering={entering}
            error={error}
            now={now}
            onEnter={() => onEnter(drop)}
          />
          <DreamWeek summary={summary} now={now} />
          {showInvite ? <InviteRow /> : null}
          {drop ? (
            <button type="button" className="dream-link" onClick={() => navigate('/dream')}>
              See the prize draw
            </button>
          ) : null}
        </aside>
      </div>
    </div>
  );
}

function DreamGate() {
  if (!apiService.isAuthenticated()) return <TempStart />;
  return <DreamPage />;
}

export default DreamGate;
